import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogIn, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { useCart } from '../context/CartContext';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { customer } = useCustomerAuth();
  const { getCartCount } = useCart();
  const cartCount = getCartCount();
  const isSignedIn = customer && customer !== false;

  // Blog lives on a separate GoDaddy-hosted subdomain (blog.iiypv.com).
  // It opens in a new tab so customers don't lose the IIY shopping flow.
  const BLOG_URL = 'https://blog.iiypv.com';

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Benefits', path: '/benefits' },
    { name: 'About', path: '/about' },
    { name: 'News', path: '/#press-releases', anchor: true },
    { name: 'FAQ', path: '/faq' },
    { name: 'Blog', path: BLOG_URL, external: true },
    { name: 'Track Order', path: '/track-order' },
    { name: 'Consult', path: '/medical-herbalist-consultation' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
    <nav className="glass-nav sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 xl:px-24">
        <div className="flex items-center justify-between h-20 gap-4">
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0" data-testid="logo-link">
            <img 
              src="/iiy-phytoventra-logo.jpg" 
              alt="IIY Phytoventra - Invest In Yourself" 
              className="h-12 w-12 object-contain group-hover:scale-105 transition-transform duration-300"
            />
            <div className="hidden md:flex flex-col leading-tight border-l border-border/60 pl-3 whitespace-nowrap">
              <span className="text-lg xl:text-xl font-bold tracking-tight" style={{ color: '#2d5016' }}>IIY <span className="text-emerald-700">Phytoventra</span></span>
              <span className="text-[9px] xl:text-[10px] uppercase tracking-[0.18em] text-secondary/80 font-medium">Invest In Yourself &reg;</span>
            </div>
          </Link>

          <div className="hidden xl:flex items-center gap-5 2xl:gap-7">
            {navLinks.map(link => link.external ? (
              <a
                key={link.path}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary hover:text-primary font-medium transition-colors duration-300 whitespace-nowrap text-[15px]"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ) : link.anchor ? (
              <a
                key={link.path}
                href={link.path}
                className="text-secondary hover:text-primary font-medium transition-colors duration-300 whitespace-nowrap text-[15px]"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                className="text-secondary hover:text-primary font-medium transition-colors duration-300 whitespace-nowrap text-[15px]"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
            {/* Mini cart icon — always visible on desktop, with live count badge */}
            <Link
              to="/cart"
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-full text-secondary hover:text-primary hover:bg-emerald-50 transition-colors duration-300"
              data-testid="nav-cart"
              aria-label={`Cart (${cartCount} items)`}
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.75} />
              {cartCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
                  data-testid="nav-cart-count"
                >
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>
            {isSignedIn ? (
              <Link
                to="/account"
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-700 text-emerald-700 hover:bg-emerald-50 font-medium transition-colors text-sm whitespace-nowrap"
                data-testid="nav-account"
              >
                {customer.picture ? (
                  <img src={customer.picture} alt="" className="w-5 h-5 rounded-full" />
                ) : (
                  <User className="w-4 h-4" />
                )}
                <span>{customer.name?.split(' ')[0] || 'Account'}</span>
              </Link>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium transition-colors text-sm whitespace-nowrap"
                data-testid="nav-signin"
              >
                <LogIn className="w-4 h-4" /> Sign in
              </Link>
            )}
          </div>

          <div className="flex items-center gap-1 xl:hidden">
            {/* Cart icon on mobile sits next to the hamburger */}
            <Link
              to="/cart"
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-lg text-secondary hover:text-primary hover:bg-emerald-50 transition-colors"
              data-testid="nav-cart-mobile"
              aria-label={`Cart (${cartCount} items)`}
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.75} />
              {cartCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-amber-600 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 hover:bg-primary/10 rounded-lg transition-colors flex-shrink-0"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-white border-t border-border"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-3">
              {navLinks.map(link => link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-secondary hover:text-primary font-medium transition-colors"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ) : link.anchor ? (
                <a
                  key={link.path}
                  href={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-secondary hover:text-primary font-medium transition-colors"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-secondary hover:text-primary font-medium transition-colors"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-3 mt-3 border-t border-slate-200">
                {isSignedIn ? (
                  <Link to="/account" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-emerald-700 font-medium" data-testid="mobile-nav-account"><User className="w-4 h-4" /> My Account</Link>
                ) : (
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-emerald-700 font-medium" data-testid="mobile-nav-signin"><LogIn className="w-4 h-4" /> Sign in / Create account</Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
    </>
  );
}
